import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Sparkles, Trash2, MessageSquare, ArrowRight, Radio } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import VoiceInput from "@/components/app/VoiceInput";

const SUGGESTIONS = [
  "Quanto ho speso in farmacia nel 2025?",
  "Elenca tutte le fatture di questo mese",
  "Qual è il totale delle spese detraibili per il 730?",
  "Ho tutti i documenti per fare l'ISEE?",
  "Chi è l'esercente da cui ho speso di più?",
  "Documenti mancanti per la dichiarazione dei redditi?",
];

export default function AssistantPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/assistant/history")
      .then(r => {
        const hist = [];
        r.data.forEach(h => {
          hist.push({ role: "user", content: h.question });
          hist.push({ role: "assistant", content: h.answer });
        });
        setMessages(hist);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setInput("");
    const newMsgs = [...messages, { role: "user", content: q }];
    setMessages(newMsgs);
    setSending(true);
    try {
      const r = await api.post("/assistant/ask", {
        message: q,
        history: newMsgs.slice(-8, -1),
      });
      setMessages(m => [...m, { role: "assistant", content: r.data.answer }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore assistente");
      setMessages(m => [...m, { role: "assistant", content: "Mi dispiace, si è verificato un errore. Riprova." }]);
    } finally { setSending(false); }
  };

  const clearHistory = async () => {
    if (!window.confirm("Cancellare la cronologia della chat?")) return;
    await api.delete("/assistant/history");
    setMessages([]);
    toast.success("Cronologia cancellata");
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-8 flex flex-col h-[calc(100dvh-3.5rem-6rem)] md:h-[calc(100vh-32px)]">
      <div className="flex items-start justify-between mb-6 shrink-0">
        <div>
          <div className="overline text-[#0A0A0A] mb-3">— ASSISTENTE AI</div>
          <h1 className="font-display font-bold text-4xl tracking-tighter mb-2">Chiedi al tuo archivio</h1>
          <p className="text-sm text-[#5C5C5C] max-w-xl">
            Fai domande in italiano sul tuo archivio: spese, categorie, esercenti, scadenze fiscali.
            L'assistente conosce ogni documento che hai caricato.
          </p>
        </div>
        {messages.length > 0 && (
          <button
            data-testid="assistant-clear-btn"
            onClick={clearHistory}
            className="text-xs text-[#8A8A8A] hover:text-[#FF3333] transition-colors flex items-center gap-1.5"
          >
            <Trash2 className="w-3.5 h-3.5" /> Cancella
          </button>
        )}
      </div>

      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto bg-white border border-black/10 rounded-xl p-6"
        data-testid="assistant-messages"
      >
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center py-12">
            <div className="w-14 h-14 bg-[#0A0A0A]/5 flex items-center justify-center rounded-xl mb-6">
              <Sparkles className="w-6 h-6 text-[#0A0A0A]" strokeWidth={1.5} />
            </div>
            <h3 className="font-display font-bold text-2xl mb-2 tracking-tight">Da dove vuoi partire?</h3>
            <p className="text-sm text-[#5C5C5C] mb-8 max-w-md">
              Alcune domande che ti aiutano a esplorare l'archivio:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full max-w-2xl">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  data-testid={`assistant-suggestion-${i}`}
                  onClick={() => send(s)}
                  disabled={sending}
                  className="text-left px-4 py-3 border border-black/10 rounded-xl hover:bg-black/5 hover:border-black/25 transition-colors text-sm group flex items-center justify-between gap-2"
                >
                  <span className="truncate">{s}</span>
                  <ArrowRight className="w-3.5 h-3.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {messages.map((m, i) => (
              <Message key={`${m.role}-${i}-${m.content.slice(0, 20)}`} role={m.role} content={m.content} />
            ))}
            {sending && <Typing />}
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="shrink-0 mt-4 flex gap-2 items-end bg-white border border-black/10 rounded-xl p-3 focus-within:border-black/30 transition-colors"
      >
        <textarea
          data-testid="assistant-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          rows={1}
          placeholder={listening ? "🎙️ Sto ascoltando…" : 'Es. "Quanto ho speso in farmacia nel 2025?"'}
          className="flex-1 bg-transparent outline-none resize-none text-sm py-2 px-2 max-h-32"
          disabled={sending}
        />
        <VoiceInput
          disabled={sending}
          onTranscript={(t) => { setInput(t); setListening(true); }}
          onFinal={(t) => {
            setListening(false);
            setInput(t);
            if (t && t.trim().length >= 2) {
              setTimeout(() => send(t), 200);
            }
          }}
        />
        <Button
          type="submit"
          data-testid="assistant-send-btn"
          disabled={sending || !input.trim()}
          className="rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white h-10 w-10 p-0"
        >
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  );
}

function Message({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "justify-end" : ""}`} data-testid={`assistant-msg-${role}`}>
      {!isUser && (
        <div className="w-8 h-8 bg-[#0A0A0A] rounded-xl flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
      )}
      <div className={`rounded-xl px-4 py-3 text-sm leading-relaxed max-w-[80%] ${
        isUser ? "bg-[#0A0A0A] text-white" : "bg-[#F8F8F6] border border-black/5"
      }`}>
        {content.split("\n").map((line, i) => (
          <p key={`${role}-line-${i}`} className={i > 0 ? "mt-2" : ""}>{line}</p>
        ))}
      </div>
      {isUser && (
        <div className="w-8 h-8 bg-[#F8F8F6] border border-black/10 rounded-xl flex items-center justify-center shrink-0">
          <MessageSquare className="w-4 h-4 text-[#0A0A0A]" />
        </div>
      )}
    </div>
  );
}

function Typing() {
  return (
    <div className="flex gap-3" data-testid="assistant-typing">
      <div className="w-8 h-8 bg-[#0A0A0A] rounded-xl flex items-center justify-center shrink-0">
        <Sparkles className="w-4 h-4 text-white animate-pulse" />
      </div>
      <div className="rounded-xl px-4 py-3 bg-[#F8F8F6] border border-black/5 flex gap-1.5">
        <span className="w-2 h-2 bg-[#0A0A0A]/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="w-2 h-2 bg-[#0A0A0A]/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
        <span className="w-2 h-2 bg-[#0A0A0A]/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
      </div>
    </div>
  );
}
